import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { IStatefulOptions } from 'src/app/core/interfaces/onboarding/page-state/stateful-options.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { EmployeeCountAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { selectEmployeeCount } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { BreakpointConstants } from 'src/app/core/ui-helpers/constants/breakpoint-constants';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-employee-count',
  templateUrl: './employee-count.component.html',
  styleUrls: ['./employee-count.component.scss'],
})
export class EmployeeCountComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  isTablet: boolean;
  employeeCountData: IStatefulOptions[] = [];

  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  questionText: string;
  properties$: Observable<any>;
  subscriptions: Subscription[] = [];
  payload: IBusinessProfile;
  pageStateId: string;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private ees: EnrollmentExtendedService,
    private bf: BusinessProfileService,
    private store: Store<IAppState>
  ) {
    super();
    this.isTablet = window.innerWidth < BreakpointConstants.tabletBreakpoint;
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            this.pageStateId = params['stateId'];
          })
        );
        this.populateOptionStates(data['options']);
      })
    );

    // Get payload from service/store
    this.payload = this.bf.getPayload();

    // Update payload with pagestate id of current page
    this.payload.PageStateId = this.pageStateId;
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  @HostListener('window:resize')
  onResize() {
    this.isTablet = window.innerWidth < BreakpointConstants.tabletBreakpoint;
  }

  async submit() {
    const res = await this.bf.submitBusinessProfileData(this.payload);
    return res.isSuccessful;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      'How many people does your company employ?';
  }

  populateOptionStates(options: any[]) {
    let selectedId = -10;
    const props$ = this.store.select(selectEmployeeCount);
    this.subscriptions.push(
      props$.subscribe((props) => {
        selectedId = props.employeeCount.id as number;
      })
    );
    this.employeeCountData = options.map((option) => ({
      ...option,
      state: this.nextState,
      selected: option.id === selectedId,
    }));
  }

  async handleOutput(outputValue: any) {
    // Handle the emitted output parameter value here

    const clickedOption = this.employeeCountData[outputValue];

    this.payload.NumberOfEmployees = clickedOption.value;
    this.payload.NumberOfEmployeesId = clickedOption.id;

    const idValuePair: { id: number; value: string } = {
      id: clickedOption.id,
      value: this.payload.NumberOfEmployees,
    };

    // saving to store
    this.store.dispatch(
      EmployeeCountAction({
        employeeCount: idValuePair,
      })
    );
  }
}
